import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { isFinite, isNumber, noop } from 'underscore';
import { onTimeBankUpgrade } from 'redux/modules/mixpanel/actions';
import { replaceModal } from 'redux/modules/modal/actions';
import { TranscriptionTimeBankUpgradeSource } from 'types/common';
import usePreviousRef from './usePreviousRef';

export interface UseBalanceOptions {
  balanceMillis: number;
  source?: TranscriptionTimeBankUpgradeSource;
  durationMillis?: number;
  transcriptionEnabled?: boolean;
  skipUntoggleOnLimitPassed?: boolean;
  onToggleTranscription?: (enabled: boolean) => void;
}

export default function useBalance({
  balanceMillis,
  source,
  durationMillis = 0,
  transcriptionEnabled = false,
  skipUntoggleOnLimitPassed,
  onToggleTranscription = noop,
}: UseBalanceOptions) {
  const newBalanceMillis = balanceMillis - durationMillis;
  const hasBalance = isNumber(newBalanceMillis) && isFinite(newBalanceMillis);
  const canTranscribe = newBalanceMillis >= 0;

  const transcriptionEnabledRef = usePreviousRef(transcriptionEnabled);
  const onToggleTranscriptionRef = usePreviousRef(onToggleTranscription);

  const cachedTranscriptionEnabled = useRef<boolean>(undefined);

  const dispatch = useDispatch();

  useEffect(() => {
    if (
      !canTranscribe &&
      transcriptionEnabledRef.current &&
      !skipUntoggleOnLimitPassed
    ) {
      cachedTranscriptionEnabled.current = true;
      onToggleTranscriptionRef.current(false);
      return;
    }

    if (
      canTranscribe &&
      cachedTranscriptionEnabled.current !== undefined &&
      !skipUntoggleOnLimitPassed
    ) {
      onToggleTranscriptionRef.current(cachedTranscriptionEnabled.current);
      cachedTranscriptionEnabled.current = undefined;
    }
  }, [
    canTranscribe,
    onToggleTranscriptionRef,
    skipUntoggleOnLimitPassed,
    transcriptionEnabledRef,
  ]);

  const onClick = useCallback(
    event => {
      event.preventDefault();
      dispatch(onTimeBankUpgrade(source));
      dispatch(
        replaceModal({
          name: 'UnlimitedTranscription',
        }),
      );
    },
    [dispatch, source],
  );

  return {
    balanceMillis,
    canTranscribe,
    hasBalance,
    newBalanceMillis,
    outOfTimeMessage: 'Not enough free transcription time left this month',
    showBalance: hasBalance && (transcriptionEnabled || newBalanceMillis <= 0),
    onClick,
  };
}
